import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import AlertComponent from "../../components/AlertComponent";
import Loading from "../../components/Loading";
import {
  FUTEBOL_TEMPLATE_ID,
  FUTEBOL_VARIANTS,
  TEMPLATES_LIST,
  TEMPLATES as TEMPLATE_COMPONENTS,
  getTemplateGroupId,
  isFutebolTemplateId,
} from "../EventoPublico/templates";
import { atualizarVariante, criarVariante, deletarVariante, listarVariantes } from "../../services/templateVariantService";
import MockRsvpForm from "../../components/MockRsvpForm";
import { buildPublicEventUrl } from "../../utils/publicEventUrl";
import { Context } from "../../Reducers/Store";
import MensagensPanel from "./MensagensPanel";
import ConvidadosEvento from "../Convidados/ConvidadosEvento";
import ListaConfirmacoes from "../Confirmacoes/ListaConfirmacoes";
import { listarWhatsappSessoesAtivas } from "../../services/mensagemService";
import { defaultLayoutConfig } from "../../utils/layoutUtils";
import { LayoutControls } from "../Layouts/LayoutsManager";
import PreviewDeviceTabs from "../../components/PreviewDeviceTabs";
import { getInitialPreviewDevice, getPreviewDeviceConfig, withPreviewDevice } from "../../utils/previewDevices";

const emptyEvento = {
  nome: "",
  slug: "",
  urlExterna: "",
  descricao: "",
  nomeEspaco: "",
  imagemUrl: "",
  dataDoEvento: "",
  dataLimiteConfirmacao: "",
  exigirCodigoConvite: false,
  localizacao: "",
  quantidadeConvidados: 0,
  idadeMinimaCobranca: 6,
  templateId: "original",
  mapaUrl: "",
  whatsappModo: 0,
  whatsappSessaoId: "",
};

function cloneLayoutConfig(config = defaultLayoutConfig) {
  return JSON.parse(JSON.stringify(config || defaultLayoutConfig));
}

function normalizeLayoutConfig(config = defaultLayoutConfig) {
  const cfg = config || {};
  const background = cfg.background || defaultLayoutConfig.background;
  return {
    ...defaultLayoutConfig,
    ...cfg,
    coverImage: {
      ...defaultLayoutConfig.coverImage,
      ...(cfg.coverImage || {}),
    },
    background: {
      ...defaultLayoutConfig.background,
      ...background,
      solid: {
        ...defaultLayoutConfig.background.solid,
        ...(background.solid || {}),
      },
      gradient: {
        ...defaultLayoutConfig.background.gradient,
        ...(background.gradient || {}),
        stops: background.gradient?.stops || defaultLayoutConfig.background.gradient.stops,
      },
      image: {
        ...defaultLayoutConfig.background.image,
        ...(background.image || {}),
      },
    },
    sections: {
      ...defaultLayoutConfig.sections,
      ...(cfg.sections || {}),
    },
  };
}

function layoutConfigSignature(config) {
  return JSON.stringify(normalizeLayoutConfig(config));
}

export default function EventoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state] = useContext(Context);
  const isEdit = !!id;

  const [form, setForm] = useState(emptyEvento);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [aba, setAba] = useState("dados");
  const [device, setDevice] = useState(getInitialPreviewDevice); // "desktop" | "tablet" | "mobile"
  const [acesso, setAcesso] = useState(null);
  const [compartilhamentos, setCompartilhamentos] = useState([]);
  const [loadingCompartilhamentos, setLoadingCompartilhamentos] = useState(false);
  const [salvandoCompartilhamento, setSalvandoCompartilhamento] = useState(false);
  const [compartilharForm, setCompartilharForm] = useState({
    email: "",
    papel: 1,
    controleTotal: false,
    podeCriarLayouts: false,
    podeEscolherWhatsapp: false,
  });
  const [sessoesWhatsapp, setSessoesWhatsapp] = useState([]);

  // Variantes disponíveis para o template selecionado (aba Aparência)
  const [aparenciaVariants, setAparenciaVariants] = useState([]);
  const [aparenciaVariantId, setAparenciaVariantId] = useState(null);
  const [aparenciaLayoutConfig, setAparenciaLayoutConfig] = useState(() => cloneLayoutConfig());
  const [aparenciaVariantNome, setAparenciaVariantNome] = useState("");
  const [salvandoAparenciaVariant, setSalvandoAparenciaVariant] = useState(false);
  const [rsvpPreviewMode, setRsvpPreviewMode] = useState("form");

  useEffect(() => {
    if (!isEdit && state?.profile && !state.profile.podeCriarEventos) {
      navigate("/eventos", { replace: true });
    }
  }, [isEdit, navigate, state?.profile]);

  useEffect(() => {
    let cancelled = false;
    listarVariantes(form.templateId, { eventId: isEdit ? Number(id) : null })
      .then(data => { if (!cancelled) setAparenciaVariants(data || []); })
      .catch(() => { if (!cancelled) setAparenciaVariants([]); });
    return () => { cancelled = true; };
  }, [form.templateId, id, isEdit]);

  useEffect(() => {
    const selected = aparenciaVariants.find((variant) => String(variant.id) === String(aparenciaVariantId));
    if (selected) {
      setAparenciaLayoutConfig(cloneLayoutConfig(selected.config));
      setAparenciaVariantNome(selected.nome || "");
      return;
    }

    if (!aparenciaVariantId) {
      setAparenciaLayoutConfig(cloneLayoutConfig());
      setAparenciaVariantNome("");
    }
  }, [aparenciaVariantId, aparenciaVariants]);

  useEffect(() => {
    let cancelled = false;
    listarWhatsappSessoesAtivas()
      .then(data => { if (!cancelled) setSessoesWhatsapp(data); })
      .catch(() => { if (!cancelled) setSessoesWhatsapp([]); });
    return () => { cancelled = true; };
  }, []);

  const carregarCompartilhamentos = useCallback(async () => {
    if (!isEdit) return;
    setLoadingCompartilhamentos(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API}/evento/${id}/compartilhamentos`);
      setCompartilhamentos(res.data || []);
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", "Erro ao carregar compartilhamentos", { icon: "error" });
    } finally {
      setLoadingCompartilhamentos(false);
    }
  }, [id, isEdit]);

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    async function carregar() {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API}/evento/${id}`);
        if (cancelled) return;
        const data = res.data;
        setAcesso(data.acesso || null);
        setForm({
          nome: data.nome || "",
          slug: data.slug || "",
          urlExterna: data.urlExterna || "",
          descricao: data.descricao || "",
          nomeEspaco: data.nomeEspaco || "",
          imagemUrl: data.imagemUrl || "",
          dataDoEvento: data.dataDoEvento ? data.dataDoEvento.slice(0, 16) : "",
          dataLimiteConfirmacao: data.dataLimiteConfirmacao ? data.dataLimiteConfirmacao.slice(0, 16) : "",
          exigirCodigoConvite: !!data.exigirCodigoConvite,
          localizacao: data.localizacao || "",
          quantidadeConvidados: data.quantidadeConvidados || 0,
          idadeMinimaCobranca: data.idadeMinimaCobranca ?? 6,
          templateId: data.templateId || "original",
          mapaUrl: data.mapaUrl || "",
          whatsappModo: data.whatsappModo ?? 0,
          whatsappSessaoId: data.whatsappSessaoId ? String(data.whatsappSessaoId) : "",
        });
        setAparenciaVariantId(data.templateVariantId ?? null);
        setAparenciaLayoutConfig(cloneLayoutConfig(data.layoutConfig));
        if (data.acesso?.podeGerenciarCompartilhamento) {
          carregarCompartilhamentos();
        }
      } catch (err) {
        console.error(err);
        AlertComponent("Erro", "Erro ao carregar evento", { icon: "error" });
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    }
    carregar();
    return () => { cancelled = true; };
  }, [carregarCompartilhamentos, id, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : type === "number" ? parseInt(value) || 0 : value,
    }));
  };

  const handleWhatsappChange = (value) => {
    if (value === "0") {
      setForm((prev) => ({ ...prev, whatsappModo: 0, whatsappSessaoId: "" }));
      return;
    }

    if (value === "3") {
      setForm((prev) => ({ ...prev, whatsappModo: 3, whatsappSessaoId: "" }));
      return;
    }

    if (value === "2") {
      setForm((prev) => ({ ...prev, whatsappModo: 2, whatsappSessaoId: "" }));
      return;
    }

    if (value.startsWith("1:")) {
      setForm((prev) => ({ ...prev, whatsappModo: 1, whatsappSessaoId: value.slice(2) }));
    }
  };

  const getAparenciaBaseConfig = () => {
    const selected = aparenciaVariants.find((variant) => String(variant.id) === String(aparenciaVariantId));
    return selected?.config || defaultLayoutConfig;
  };

  const aparenciaFoiAlterada = () =>
    layoutConfigSignature(aparenciaLayoutConfig) !== layoutConfigSignature(getAparenciaBaseConfig());

  const usuarioPodeEditarAparenciaEvento = () =>
    isEdit ? !!acesso?.podeCriarLayouts : !!state?.profile?.podeCriarLayouts;

  const confirmarDescarteAparenciaSeNecessario = () => {
    if (!isEdit || !usuarioPodeEditarAparenciaEvento() || !aparenciaFoiAlterada()) return true;
    return window.confirm("Você tem alterações de aparência não salvas. Deseja descartar essas alterações?");
  };

  const selecionarTemplate = (templateId) => {
    if (!confirmarDescarteAparenciaSeNecessario()) return;
    setForm((prev) => ({ ...prev, templateId }));
    setAparenciaVariantId(null);
    setAparenciaVariantNome("");
    setAparenciaLayoutConfig(cloneLayoutConfig());
  };

  const selecionarFutebolVariant = (templateId) => {
    if (!confirmarDescarteAparenciaSeNecessario()) return;
    setForm((prev) => ({ ...prev, templateId }));
    setAparenciaVariantId(null);
    setAparenciaVariantNome("");
    setAparenciaLayoutConfig(cloneLayoutConfig());
  };

  const selecionarAparenciaVariant = (variantId) => {
    if (!confirmarDescarteAparenciaSeNecessario()) return;
    if (!variantId) {
      setAparenciaVariantId(null);
      setAparenciaVariantNome("");
      setAparenciaLayoutConfig(cloneLayoutConfig());
      return;
    }

    const variant = aparenciaVariants.find((item) => String(item.id) === String(variantId));
    setAparenciaVariantId(variantId);
    setAparenciaVariantNome(variant?.nome || "");
    setAparenciaLayoutConfig(cloneLayoutConfig(variant?.config));
  };

  const recarregarAparenciaVariants = async (selectId = null) => {
    const data = await listarVariantes(form.templateId, { eventId: isEdit ? Number(id) : null });
    setAparenciaVariants(data || []);
    if (selectId) {
      setAparenciaVariantId(selectId);
    }
  };

  const salvarAparenciaVariantEvento = async ({ silent = false, nomeFallback = "" } = {}) => {
    if (!isEdit) {
      if (!silent) {
        AlertComponent("Salve o evento primeiro", "As versões específicas ficam vinculadas a um evento já criado.", { icon: "warning" });
      }
      return null;
    }

    const nome = aparenciaVariantNome.trim() || nomeFallback;
    if (!nome) {
      if (!silent) {
        AlertComponent("Nome obrigatório", "Informe um nome para esta versão do layout.", { icon: "warning" });
      }
      return null;
    }

    const selected = aparenciaVariants.find((variant) => String(variant.id) === String(aparenciaVariantId));
    setSalvandoAparenciaVariant(true);
    try {
      const saved = selected?.eventId
        ? await atualizarVariante(selected.id, nome, aparenciaLayoutConfig)
        : await criarVariante(form.templateId, nome, aparenciaLayoutConfig, Number(id));

      await recarregarAparenciaVariants(saved.id);
      if (!silent) {
        AlertComponent("Versão salva", "Esta versão ficou disponível apenas neste evento.", { icon: "success", timer: 1800 });
      }
      return saved;
    } catch (err) {
      console.error(err);
      if (!silent) {
        AlertComponent("Erro", err.response?.data?.mensagem || "Erro ao salvar versão do evento", { icon: "error" });
      }
      throw err;
    } finally {
      setSalvandoAparenciaVariant(false);
    }
  };

  const excluirAparenciaVariantEvento = async () => {
    const selected = aparenciaVariants.find((variant) => String(variant.id) === String(aparenciaVariantId));
    if (!selected?.eventId) return;

    const confirmado = window.confirm(`Excluir a versão "${selected.nome}" deste evento?`);
    if (!confirmado) return;

    setSalvandoAparenciaVariant(true);
    try {
      await deletarVariante(selected.id);
      setAparenciaVariantId(null);
      setAparenciaVariantNome("");
      setAparenciaLayoutConfig(cloneLayoutConfig());
      await recarregarAparenciaVariants(null);
      AlertComponent("Versão excluída", "A versão foi removida apenas deste evento.", { icon: "success", timer: 1600 });
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", err.response?.data?.mensagem || "Erro ao excluir versão do evento", { icon: "error" });
    } finally {
      setSalvandoAparenciaVariant(false);
    }
  };

  useEffect(() => {
    if (aba === "mensagens" && Number(form.whatsappModo) === 0) {
      setAba("dados");
    }
  }, [aba, form.whatsappModo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let templateVariantId = aparenciaVariantId;
      let aparenciaSalvaAutomaticamente = false;

      if (isEdit && usuarioPodeEditarAparenciaEvento() && aparenciaFoiAlterada()) {
        const savedAppearance = await salvarAparenciaVariantEvento({
          silent: true,
          nomeFallback: aparenciaVariantNome.trim() || "Aparência personalizada",
        });
        if (savedAppearance?.id) {
          templateVariantId = savedAppearance.id;
          aparenciaSalvaAutomaticamente = true;
        }
      }

      const payload = {
        nome: form.nome,
        slug: form.slug || null,
        urlExterna: form.urlExterna || null,
        descricao: form.descricao || null,
        nomeEspaco: form.nomeEspaco || null,
        imagemUrl: form.imagemUrl || null,
        dataDoEvento: form.dataDoEvento,
        dataLimiteConfirmacao: form.dataLimiteConfirmacao || null,
        exigirCodigoConvite: form.exigirCodigoConvite,
        localizacao: form.localizacao,
        quantidadeConvidados: form.quantidadeConvidados,
        idadeMinimaCobranca: form.idadeMinimaCobranca,
        templateId: form.templateId,
        mapaUrl: form.mapaUrl || null,
        templateVariantId,
        whatsappModo: Number(form.whatsappModo) || 0,
        whatsappSessaoId: Number(form.whatsappModo) === 1 && form.whatsappSessaoId ? Number(form.whatsappSessaoId) : null,
      };
      if (isEdit) {
        await axios.put(`${process.env.REACT_APP_API}/evento/${id}`, payload);
        AlertComponent("Sucesso", aparenciaSalvaAutomaticamente ? "Evento e aparência salvos com sucesso!" : "Evento salvo com sucesso!", { icon: "success" });
      } else {
        await axios.post(`${process.env.REACT_APP_API}/evento`, payload);
        AlertComponent("Sucesso", "Evento criado com sucesso!", { icon: "success" });
      }
      navigate("/eventos");
    } catch (error) {
      console.error(error);
      AlertComponent("Erro", error?.response?.data?.message ?? error?.response?.data?.mensagem ?? "Erro ao salvar evento", { icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleConvidar = async () => {
    if (!compartilharForm.email.trim()) {
      AlertComponent("E-mail obrigatório", "Informe o e-mail do usuário", { icon: "warning" });
      return;
    }

    setSalvandoCompartilhamento(true);
    try {
      const payload = {
        email: compartilharForm.email.trim(),
        papel: 1,
        controleTotal: !!compartilharForm.controleTotal,
        podeCriarLayouts: !!compartilharForm.controleTotal || compartilharForm.podeCriarLayouts,
        podeEscolherWhatsapp: !!compartilharForm.controleTotal || compartilharForm.podeEscolherWhatsapp,
      };
      const res = await axios.post(`${process.env.REACT_APP_API}/evento/${id}/compartilhamentos`, payload);
      const emailEnviado = !!res.data?.emailEnviado;
      const emailErro = res.data?.emailErro;
      if (res.data?.linkAceite) {
        await navigator.clipboard?.writeText(res.data.linkAceite);
        AlertComponent(
          emailEnviado ? "Convite enviado" : "Convite criado",
          emailEnviado
            ? "O convite foi enviado por e-mail e o link de aceite foi copiado."
            : `Não foi possível enviar o e-mail${emailErro ? `: ${emailErro}` : ""}. O link de aceite foi copiado.`,
          { icon: emailEnviado ? "success" : "warning" }
        );
      } else {
        AlertComponent(
          "Acesso liberado",
          emailEnviado
            ? "O evento já aparece para este usuário e o aviso foi enviado por e-mail."
            : `O evento já aparece para este usuário${emailErro ? `, mas o e-mail não foi enviado: ${emailErro}` : "."}`,
          { icon: emailEnviado ? "success" : "warning", timer: emailEnviado ? 1700 : undefined }
        );
      }
      setCompartilharForm({ email: "", papel: 1, controleTotal: false, podeCriarLayouts: false, podeEscolherWhatsapp: false });
      await carregarCompartilhamentos();
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", err.response?.data?.mensagem || "Erro ao compartilhar evento", { icon: "error" });
    } finally {
      setSalvandoCompartilhamento(false);
    }
  };

  const handleRevogar = async (compartilhamentoId) => {
    setSalvandoCompartilhamento(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API}/evento/${id}/compartilhamentos/${compartilhamentoId}`);
      AlertComponent("Removido", "Compartilhamento removido", { icon: "success", timer: 1500 });
      await carregarCompartilhamentos();
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", err.response?.data?.mensagem || "Erro ao remover compartilhamento", { icon: "error" });
    } finally {
      setSalvandoCompartilhamento(false);
    }
  };

  const handleCopiarLinkAceite = async (linkAceite) => {
    if (!linkAceite) return;

    try {
      await navigator.clipboard?.writeText(linkAceite);
      AlertComponent("Link copiado", "Link de aceite copiado para a área de transferência", { icon: "success", timer: 1600 });
    } catch {
      AlertComponent("Link de aceite", linkAceite, { icon: "info" });
    }
  };

  const handleCancelar = () => {
    if (!confirmarDescarteAparenciaSeNecessario()) return;
    navigate("/eventos");
  };

  const selectedTemplateGroupId = getTemplateGroupId(form.templateId);
  const futebolSelecionado = isFutebolTemplateId(form.templateId);
  const TemplateComponent = TEMPLATE_COMPONENTS[form.templateId];
  const previewDevice = getPreviewDeviceConfig(device);
  const previewLayoutConfig = withPreviewDevice(aparenciaLayoutConfig, device);
  const selectedAparenciaVariant = aparenciaVariants.find((variant) => String(variant.id) === String(aparenciaVariantId));
  const aparenciaAlterada = isEdit && usuarioPodeEditarAparenciaEvento() && aparenciaFoiAlterada();
  const aparenciaVariantsEvento = aparenciaVariants.filter((variant) => !!variant.eventId);
  const aparenciaVariantsGlobais = aparenciaVariants.filter((variant) => !variant.eventId);
  const previewEvento = {
    nome: form.nome || "Nome do Evento",
    descricao: form.descricao || "",
    dataDoEvento: form.dataDoEvento || "2026-06-15T16:00:00",
    dataLimiteConfirmacao: form.dataLimiteConfirmacao || "2026-04-23T22:00:00",
    localizacao: form.localizacao || "Localização do evento",
    nomeEspaco: form.nomeEspaco || "",
    imagemUrl: form.imagemUrl || null,
    mapaUrl: form.mapaUrl || null,
    isActive: true,
  };

  if (loadingData) return <Loading />;

  const podeCriarLayouts = !!state?.profile?.podeCriarLayouts;
  const podeEditarLayoutEvento = isEdit ? !!acesso?.podeCriarLayouts : podeCriarLayouts;
  const podeGerenciarCompartilhamento = !!acesso?.podeGerenciarCompartilhamento;
  const podeEscolherWhatsapp = !isEdit || !!acesso?.podeEscolherWhatsapp;
  const whatsappAtivoNoEvento = Number(form.whatsappModo) !== 0;
  const whatsappSelectValue = Number(form.whatsappModo) === 1
    ? `1:${form.whatsappSessaoId || ""}`
    : String(Number(form.whatsappModo) || 0);
  const sessaoSelecionadaEstaNaLista = sessoesWhatsapp.some((sessao) => String(sessao.id) === String(form.whatsappSessaoId));
  const tabs = [
    { key: "dados", label: "Dados do Evento" },
    { key: "aparencia", label: "Aparência" },
    ...(isEdit ? [
      { key: "convidados", label: "Convidados" },
      { key: "confirmacoes", label: "Confirmações" },
      ...(whatsappAtivoNoEvento ? [{ key: "mensagens", label: "Mensagens" }] : []),
    ] : []),
    ...(podeGerenciarCompartilhamento ? [{ key: "compartilhamento", label: "Compartilhamento" }] : []),
  ];

  return (
    <div className={`w-full min-w-0 ${aba === "aparencia" ? "lg:h-[calc(100dvh-8rem)] lg:flex lg:flex-col lg:overflow-hidden" : ""}`}>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-800">
          {isEdit ? "Editar Evento" : "Novo Evento"}
        </h1>
        <p className="text-sm text-slate-500">
          {isEdit ? "Atualize as informações do evento" : "Preencha as informações para criar um novo evento"}
        </p>
      </div>

      {/* Abas */}
      <div className="flex gap-1 mb-6 border-b overflow-x-auto">
        {tabs.map((tab) => (
          <button key={tab.key} type="button" onClick={() => setAba(tab.key)}
            className={`shrink-0 px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              aba === tab.key
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {(aba === "dados" || aba === "aparencia") && (
      <form
        onSubmit={handleSubmit}
        className={aba === "aparencia" ? "lg:flex lg:flex-col lg:flex-1 lg:min-h-0 lg:overflow-hidden" : ""}
      >
        {/* Aba Dados */}
        {aba === "dados" && (
          <div className="bg-white p-8 rounded-xl shadow border">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do evento <span className="text-red-500">*</span>
                </label>
                <input type="text" name="nome" value={form.nome} onChange={handleChange}
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                  placeholder="Ex: Festa de Aniversário da Julia" required disabled={loading} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug (URL amigável)</label>
                <input type="text" name="slug" value={form.slug} onChange={handleChange}
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                  placeholder="deixe vazio para gerar automaticamente" disabled={loading} />
                <p className="text-xs text-slate-400 mt-1">Gerado automaticamente a partir do nome se vazio</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL externa</label>
                <input type="text" name="urlExterna" value={form.urlExterna} onChange={handleChange}
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                  placeholder="julia.jabburelias.com.br" disabled={loading} />
                <p className="text-xs text-slate-400 mt-1">Use só um domínio ou cole a URL completa; o sistema salva apenas o host</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data e hora <span className="text-red-500">*</span>
                </label>
                <input type="datetime-local" name="dataDoEvento" value={form.dataDoEvento} onChange={handleChange}
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                  required disabled={loading} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data limite para confirmação</label>
                <input type="datetime-local" name="dataLimiteConfirmacao" value={form.dataLimiteConfirmacao} onChange={handleChange}
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                  disabled={loading} />
                <p className="text-xs text-slate-400 mt-1">Após esta data o formulário de confirmação será encerrado</p>
              </div>

              <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="exigirCodigoConvite"
                    checked={form.exigirCodigoConvite}
                    onChange={handleChange}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-300"
                    disabled={loading}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-slate-800">Evento fechado</span>
                    <span className="block text-xs text-slate-500 mt-1">
                      Exige código individual antes de exibir o convite e liberar a confirmação.
                    </span>
                  </span>
                </label>
              </div>

              {podeEscolherWhatsapp && (
                <div className="md:col-span-2 rounded-lg border border-slate-200 bg-white p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp utilizado neste evento</label>
                  <select
                    value={whatsappSelectValue}
                    onChange={(e) => handleWhatsappChange(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                    disabled={loading}
                  >
                    <option value="0">Não utilizar</option>
                    <option value="3">WhatsApp do sistema</option>
                    {Number(form.whatsappModo) === 1 && form.whatsappSessaoId && !sessaoSelecionadaEstaNaLista && (
                      <option value={`1:${form.whatsappSessaoId}`}>Sessão selecionada neste evento</option>
                    )}
                    {sessoesWhatsapp.map((sessao) => (
                      <option key={sessao.id} value={`1:${sessao.id}`}>
                        {sessao.nome}{sessao.telefone ? ` - ${sessao.telefone}` : ""}
                      </option>
                    ))}
                    <option value="2">Configurar Novo WhatsApp</option>
                  </select>
                  <p className="text-xs text-slate-400 mt-1">
                    Em “Não utilizar”, o evento não envia WhatsApp e a aba Mensagens fica oculta.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do espaço
                </label>
                <input type="text" name="nomeEspaco" value={form.nomeEspaco} onChange={handleChange}
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                  placeholder="Ex: Buffet Alegria, Minha casa" disabled={loading} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Localização <span className="text-red-500">*</span>
                </label>
                <input type="text" name="localizacao" value={form.localizacao} onChange={handleChange}
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                  placeholder="Ex: Rua das Flores, 123 - São Paulo" required disabled={loading} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Capacidade de convidados</label>
                <input type="number" name="quantidadeConvidados" value={form.quantidadeConvidados} onChange={handleChange}
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                  min="0" disabled={loading} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Idade mínima para cobrança (crianças)</label>
                <input type="number" name="idadeMinimaCobranca" value={form.idadeMinimaCobranca} onChange={handleChange}
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                  min="0" max="18" disabled={loading} />
                <p className="text-xs text-slate-400 mt-1">Crianças com idade igual ou superior pagam ingresso. Padrão: 6 anos</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Link do Google Maps</label>
                <input type="url" name="mapaUrl" value={form.mapaUrl} onChange={handleChange}
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                  placeholder="https://maps.google.com/... (opcional)" disabled={loading} />
                <p className="text-xs text-slate-400 mt-1">Se vazio, gera automaticamente a partir do endereço</p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição / Subtítulo
                </label>
                <textarea name="descricao" value={form.descricao} onChange={handleChange} rows={3}
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300 resize-none"
                  placeholder="Ex: '1 ano', 'Casamento', 'Formatura' — até 25 caracteres vira balão no template Original"
                  disabled={loading} />
              </div>
            </div>
          </div>
        )}

        {/* Aba Aparência */}
        {aba === "aparencia" && (
          <div className="flex flex-col gap-6 items-start lg:flex-row lg:items-stretch lg:flex-1 lg:min-h-0">

            {/* Esquerda: seleção de template */}
            <div className="w-full lg:w-96 shrink-0 lg:min-h-0">
              <div className="bg-white rounded-xl shadow border p-6 lg:h-full lg:overflow-y-auto">
                <h2 className="text-base font-semibold text-slate-800 mb-1">Layout do convite</h2>
                <p className="text-sm text-slate-500 mb-5">Escolha o visual da página pública.</p>
                <div className="flex flex-row lg:flex-col gap-3 overflow-x-auto lg:overflow-x-visible pb-1 lg:pb-0">
                  {TEMPLATES_LIST.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => selecionarTemplate(t.id)}
                      className={`p-3 rounded-xl border-2 text-left transition-all shrink-0 w-44 lg:w-auto ${
                        selectedTemplateGroupId === t.id
                          ? "border-slate-900 bg-slate-50 shadow-md"
                          : "border-gray-200 hover:border-gray-400"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{t.emoji}</span>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-800 text-sm">{t.label}</div>
                          <div className="text-xs text-slate-500 truncate">{t.desc}</div>
                        </div>
                        {selectedTemplateGroupId === t.id && (
                          <span className="ml-auto text-xs font-bold text-slate-900 shrink-0">✓</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Variante de layout */}
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Variante</p>
                    {podeCriarLayouts && (
                      <Link to="/layouts" className="text-xs text-violet-600 hover:text-violet-700 font-medium">
                        Globais →
                      </Link>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => futebolSelecionado ? selecionarFutebolVariant(FUTEBOL_TEMPLATE_ID) : selecionarAparenciaVariant(null)}
                      className={`px-3 py-2 rounded-lg text-sm text-left transition-all ${!aparenciaVariantId && (!futebolSelecionado || form.templateId === FUTEBOL_TEMPLATE_ID) ? "bg-slate-100 font-medium text-slate-800" : "text-slate-500 hover:bg-gray-50"}`}
                    >
                      {futebolSelecionado ? "São Paulo padrão" : "Padrão do tema"}
                    </button>
                    {futebolSelecionado && FUTEBOL_VARIANTS.map((variant) => (
                      <button
                        key={variant.id}
                        type="button"
                        onClick={() => selecionarFutebolVariant(variant.id)}
                        className={`px-3 py-2 rounded-lg text-sm text-left transition-all ${!aparenciaVariantId && form.templateId === variant.id ? "bg-slate-100 font-medium text-slate-800" : "text-slate-500 hover:bg-gray-50"}`}
                      >
                        <span className="block">{variant.label}</span>
                        <span className="block text-xs text-slate-400">{variant.desc}</span>
                      </button>
                    ))}
                    {aparenciaVariantsEvento.length > 0 && (
                      <p className="pt-2 text-[11px] font-semibold uppercase tracking-wider text-emerald-600">Deste evento</p>
                    )}
                    {aparenciaVariantsEvento.map(v => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => selecionarAparenciaVariant(v.id)}
                        className={`px-3 py-2 rounded-lg text-sm text-left transition-all ${String(aparenciaVariantId) === String(v.id) ? "bg-emerald-50 font-medium text-emerald-800" : "text-slate-500 hover:bg-gray-50"}`}
                      >
                        {v.nome}
                      </button>
                    ))}
                    {aparenciaVariantsGlobais.length > 0 && (
                      <p className="pt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Globais</p>
                    )}
                    {aparenciaVariantsGlobais.map(v => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => selecionarAparenciaVariant(v.id)}
                        className={`px-3 py-2 rounded-lg text-sm text-left transition-all ${String(aparenciaVariantId) === String(v.id) ? "bg-slate-100 font-medium text-slate-800" : "text-slate-500 hover:bg-gray-50"}`}
                      >
                        {v.nome}
                      </button>
                    ))}
                    {aparenciaVariants.length === 0 && !futebolSelecionado && (
                      <p className="text-xs text-slate-400 mt-1">
                        Nenhuma variante criada para este tema.
                      </p>
                    )}
                  </div>
                </div>

                {podeEditarLayoutEvento && isEdit && (
                  <div className="mt-5 pt-4 border-t border-gray-100 space-y-4">
                    <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
                      <h3 className="text-sm font-semibold text-emerald-900 mb-1">
                        {selectedAparenciaVariant?.eventId ? "Editar versão deste evento" : "Criar versão deste evento"}
                      </h3>
                      <p className="text-xs text-emerald-700 mb-3">
                        As alterações abaixo ficam disponíveis apenas neste evento.
                      </p>
                      {aparenciaAlterada && (
                        <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
                          Há alterações de aparência não salvas. Ao salvar o evento, uma versão deste evento será criada ou atualizada automaticamente.
                        </p>
                      )}
                      <input
                        type="text"
                        value={aparenciaVariantNome}
                        onChange={(e) => setAparenciaVariantNome(e.target.value)}
                        placeholder='Ex: "Versão criança", "Versão adulto"'
                        className="w-full rounded-lg border border-emerald-100 px-3 py-2 text-sm outline-none focus:border-emerald-300 mb-3"
                        disabled={salvandoAparenciaVariant}
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => salvarAparenciaVariantEvento()}
                          disabled={salvandoAparenciaVariant}
                          className="flex-1 rounded-lg bg-emerald-700 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60"
                        >
                          {salvandoAparenciaVariant
                            ? "Salvando..."
                            : selectedAparenciaVariant?.eventId
                              ? "Atualizar versão"
                              : "Salvar versão"}
                        </button>
                        {selectedAparenciaVariant?.eventId && (
                          <button
                            type="button"
                            onClick={excluirAparenciaVariantEvento}
                            disabled={salvandoAparenciaVariant}
                            className="rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60"
                          >
                            Excluir
                          </button>
                        )}
                      </div>
                    </div>

                    <LayoutControls
                      layoutCfg={aparenciaLayoutConfig}
                      setLayoutCfg={setAparenciaLayoutConfig}
                      templateId={form.templateId}
                    />
                  </div>
                )}

                {!isEdit && (
                  <p className="mt-4 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
                    Para criar versões específicas, salve o evento primeiro e volte em Aparência.
                  </p>
                )}
              </div>
            </div>

            {/* Direita: Preview */}
            <div className="flex-1 min-w-0 w-full bg-white rounded-xl shadow border overflow-hidden flex flex-col min-h-[720px] lg:min-h-0 lg:h-full">

              {/* Barra de dispositivos */}
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-b bg-gray-50 shrink-0">
                <div className="flex flex-wrap items-center gap-3">
                  <PreviewDeviceTabs value={device} onChange={setDevice} />

                  <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
                    {[
                      { key: "form", label: "Formulário" },
                      { key: "invite", label: "Evento fechado" },
                      { key: "closed", label: "Prazo encerrado" },
                    ].map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => setRsvpPreviewMode(option.key)}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                          rsvpPreviewMode === option.key
                            ? "bg-slate-900 text-white"
                            : "text-slate-500 hover:text-slate-700"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                {(form.slug || form.urlExterna) && (
                  <a
                    href={buildPublicEventUrl({ slug: form.slug, urlExterna: form.urlExterna })}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-slate-500 hover:text-slate-800 underline"
                  >
                    Abrir em nova aba ↗
                  </a>
                )}
              </div>

              {/* Preview area */}
              <div className="flex-1 min-h-0 bg-gray-100 overflow-auto">
                <div className="min-h-full px-4 py-6 flex justify-center">
                  <div
                    className={`bg-white shadow-lg ring-1 ring-slate-200 overflow-hidden transition-all duration-300 ${previewDevice.frameClass}`}
                    style={{
                      width: previewDevice.width,
                      maxWidth: previewDevice.maxWidth,
                      minHeight: previewDevice.minHeight,
                      pointerEvents: "none",
                      userSelect: "none",
                    }}
                  >
                    {TemplateComponent && (
                      <TemplateComponent evento={previewEvento} layoutConfig={previewLayoutConfig} templateId={form.templateId}>
                        <MockRsvpForm
                          templateId={form.templateId}
                          layoutConfig={previewLayoutConfig}
                          previewMode={rsvpPreviewMode}
                          closedDeadline={previewEvento.dataLimiteConfirmacao}
                        />
                      </TemplateComponent>
                    )}
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}

        <div className="mt-8 flex justify-end gap-3 border-t pt-6 shrink-0">
          <button type="button" onClick={handleCancelar}
            className="px-5 py-2 rounded-md border hover:bg-gray-50 disabled:opacity-50" disabled={loading}>
            Cancelar
          </button>
          <button type="submit"
            className="px-6 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50" disabled={loading}>
            {loading ? "Salvando..." : isEdit ? "Salvar alterações" : "Criar evento"}
          </button>
        </div>
      </form>
      )}

      {aba === "compartilhamento" && (
        <CompartilhamentoPanel
          form={compartilharForm}
          setForm={setCompartilharForm}
          compartilhamentos={compartilhamentos}
          loading={loadingCompartilhamentos}
          salvando={salvandoCompartilhamento}
          onConvidar={handleConvidar}
          onRevogar={handleRevogar}
          onCopiarLinkAceite={handleCopiarLinkAceite}
        />
      )}

      {aba === "mensagens" && (
        <MensagensPanel
          eventId={Number(id)}
          onWhatsappConfigChange={(config) => {
            if (!config) return;
            setForm((prev) => ({
              ...prev,
              whatsappModo: config.whatsappModo ?? prev.whatsappModo,
              whatsappSessaoId: config.whatsappSessaoId ? String(config.whatsappSessaoId) : "",
            }));
          }}
        />
      )}

      {aba === "convidados" && (
        <ConvidadosEvento eventId={id} embedded />
      )}

      {aba === "confirmacoes" && (
        <ListaConfirmacoes eventId={id} embedded />
      )}
    </div>
  );
}

function CompartilhamentoPanel({
  form,
  setForm,
  compartilhamentos,
  loading,
  salvando,
  onConvidar,
  onRevogar,
  onCopiarLinkAceite,
}) {
  return (
    <div className="bg-white p-6 rounded-xl shadow border">
      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
        <div className="rounded-xl border border-slate-200 p-4 h-fit">
          <h2 className="text-base font-semibold text-slate-800 mb-1">Compartilhar evento</h2>
          <p className="mb-4 text-xs text-slate-500">
            A pessoa entra como colaboradora deste evento, com as permissões abaixo.
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">E-mail</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                disabled={salvando}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Controle no evento</label>
              <select
                value={form.controleTotal ? "total" : "parcial"}
                onChange={(e) => {
                  const controleTotal = e.target.value === "total";
                  setForm((p) => ({
                    ...p,
                    controleTotal,
                    podeCriarLayouts: controleTotal ? true : p.podeCriarLayouts,
                    podeEscolherWhatsapp: controleTotal ? true : p.podeEscolherWhatsapp,
                  }));
                }}
                disabled={salvando}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
              >
                <option value="total">Controle Total</option>
                <option value="parcial">Controle Parcial</option>
              </select>
            </div>
            <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 text-sm">
              <input
                type="checkbox"
                checked={form.controleTotal || form.podeCriarLayouts}
                onChange={(e) => setForm((p) => ({ ...p, podeCriarLayouts: e.target.checked }))}
                disabled={salvando || form.controleTotal}
                className="mt-0.5"
              />
              <span>
                <span className="block font-semibold text-slate-700">Pode editar layout deste evento</span>
                <span className="block text-xs text-slate-500 mt-0.5">Permite criar versões de layout restritas a este evento.</span>
              </span>
            </label>
            <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 text-sm">
              <input
                type="checkbox"
                checked={form.controleTotal || form.podeEscolherWhatsapp}
                onChange={(e) => setForm((p) => ({ ...p, podeEscolherWhatsapp: e.target.checked }))}
                disabled={salvando || form.controleTotal}
                className="mt-0.5"
              />
              <span>
                <span className="block font-semibold text-slate-700">Pode escolher WhatsApp</span>
                <span className="block text-xs text-slate-500 mt-0.5">Permite selecionar sessão, desativar envios ou configurar um novo WhatsApp.</span>
              </span>
            </label>
            <button
              type="button"
              onClick={onConvidar}
              disabled={salvando}
              className="w-full rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {salvando ? "Enviando..." : "Compartilhar evento"}
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" />
            </div>
          ) : compartilhamentos.length === 0 ? (
            <div className="text-center py-16 px-6 text-sm text-slate-500">
              Nenhum usuário compartilhado neste evento.
            </div>
          ) : (
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Usuário</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Acesso</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {compartilhamentos.map((item) => (
                  <tr key={item.id}>
                    <td className="px-4 py-4">
                      <p className="text-sm font-semibold text-slate-800">{item.nomeUsuario || "Convite pendente"}</p>
                      <p className="text-xs text-slate-500">{item.email}</p>
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-600">
                      <p>{item.controleTotal ? "Controle Total" : "Controle Parcial"}</p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(item.controleTotal || item.podeCriarLayouts) && (
                          <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700">Layouts</span>
                        )}
                        {(item.controleTotal || item.podeEscolherWhatsapp) && (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">WhatsApp</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${statusClass(item.status)}`}>
                        {statusLabel(item.status)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex justify-center gap-2">
                        {item.linkAceite && (
                          <button
                            type="button"
                            onClick={() => onCopiarLinkAceite(item.linkAceite)}
                            disabled={salvando}
                            className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                          >
                            Copiar link
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => onRevogar(item.id)}
                          disabled={salvando}
                          className="px-3 py-1.5 rounded-lg border border-red-200 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60"
                        >
                          Remover
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function statusLabel(status) {
  if (status === 1) return "Aceito";
  if (status === 2) return "Revogado";
  return "Pendente";
}

function statusClass(status) {
  if (status === 1) return "bg-emerald-100 text-emerald-700";
  if (status === 2) return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-700";
}
